import React, { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import isEqual from 'lodash/isEqual';
import { getEmptyADF } from '@atlaskit/adf-utils/empty-adf';
import { useEntryPoint } from '@atlassian/jira-entry-point';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useFlagsService } from '@atlassian/jira-flags';
import {
	fireUIAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { ADF } from '@atlassian/jira-rich-content';
import type { CommentOnFlaggingUnflaggingSafeEntryPointProps } from '@atlassian/jira-software-add-comment-on-flag-action';
import { asyncFlagWithCommentModal } from '@atlassian/jira-software-add-comment-on-flag-action/entrypoint';
import { useFlagIssueModal } from '../controllers/flag-with-comment';
import { saveFlagWithComment } from '../services/save-flag-with-comment';
import { messages } from './messages';

const entryPointParams = {};
const FlagIssueWithCommentModalBase: FC = () => {
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncFlagWithCommentModal,
		entryPointParams,
	);

	const [initialized, setInitialized] = useState(false);
	const [{ flagAction, issues, forcePreload, callback, analyticsArgs }, { close }] =
		useFlagIssueModal();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();

	useEffect(() => {
		if (forcePreload && !initialized) {
			setInitialized(true);
			entryPointActions.preload();
		}
	}, [entryPointActions, forcePreload, initialized]);

	useEffect(() => {
		if (flagAction !== undefined) {
			entryPointActions.load();
			if (!initialized) {
				setInitialized(true);
			}
		} else if (initialized) {
			entryPointActions.unload();
		}
	}, [flagAction, initialized, entryPointActions]);

	const issueIds = useMemo(() => issues.map(({ issueId }) => issueId), [issues]);

	// Not using issueIds from modal because it is number[] where they should be IssueId[]
	const handleSaveWithComment = useCallback(
		async (_: number[], flag: boolean, comment: ADF) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});

			const extendedAnalyticsArgs = {
				hasComment: !isEqual(comment, getEmptyADF()),
				...analyticsArgs,
			};

			fireUIAnalytics(analyticsEvent, 'confirmButton', extendedAnalyticsArgs);

			close();

			try {
				await saveFlagWithComment(issueIds, flag, comment);

				analyticsEvent.update({
					action: 'flagged',
					actionSubject: 'cards',
				});

				fireTrackAnalytics(analyticsEvent, 'cards flagged', {
					...extendedAnalyticsArgs,

					numCards: issueIds.length,
					flagAction: flag ? 'added' : 'removed',
				});

				if (callback) {
					callback({ issueIds, flag, comment });
				}
			} catch (err) {
				const error = err instanceof Error ? err : new Error('Unknown error');

				fireErrorAnalytics({
					error,
					meta: {
						id: 'jira.addCommentOnFlagSaveAction',
						packageName: 'jiraSoftwareAddIssueFlagWithComment',
						teamName: 'deliveroo',
					},
				});

				showFlag({
					key: 'flag-issue-with-comment-modal-error',
					type: 'error',
					title: flag ? messages.addFlagFailedTitle : messages.removeFlagFailedTitle,
					description: flag
						? messages.addFlagFailedDescription
						: messages.removeFlagFailedDescription,
				});
			}
		},
		[createAnalyticsEvent, analyticsArgs, close, issueIds, callback, showFlag],
	);

	const handleOnClose = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});

		fireUIAnalytics(analyticsEvent, 'cancelButton', {
			...analyticsArgs,
		});

		close();
	};

	const modalProps: CommentOnFlaggingUnflaggingSafeEntryPointProps = {
		flagAction,
		cards: issues.map(({ issueId, issueKey, summary, type }) => ({
			id: +issueId, // Parsing issueId because FlagWithCommentModal expects number. However this is not used when saving as we are using the IssueIds from the state
			key: issueKey,
			summary: summary ?? '',
			type,
		})),
		onClose: handleOnClose,
		onFlagWithComment: handleSaveWithComment,
	};

	return (
		<ModalEntryPointContainer
			entryPointActions={entryPointActions}
			entryPointReferenceSubject={entryPointReferenceSubject}
			id="flag-issue-with-comment-modal"
			packageName="jira-software-add-issue-flag-with-comment"
			runtimeProps={modalProps}
		/>
	);
};

export const FlagIssueWithCommentModal: FC = () => (
	<JSErrorBoundary
		id="jira-software-add-issue-flag-with-comment-entrypoint"
		packageName="JiraSoftwareAddIssueFlagWithComment"
		fallback="unmount"
		teamName="deliveroo"
	>
		<FlagIssueWithCommentModalBase />
	</JSErrorBoundary>
);
